import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * A route that renders `UnifiedShellFrame` itself, where neither `useUnifiedShellFrameActive` nor a
 * path test can tell the header so in advance. The catch-all "Page not found" route is the case: it
 * matches whatever no other route does, so there is no path to test, and without this the header
 * would paint its own bar above the frame's. Below tablet that bar is the legacy `MobileHeader`,
 * which is how an unknown URL on a phone came to show the dark header and legacy footer.
 *
 * A module-level count rather than a context, because the header (`root/main/header-maybe.tsx`) is
 * a sibling of the route tree, not an ancestor of it. The claim is taken in a layout effect, so the
 * header re-renders before the first paint and the other bar never shows.
 */
let claims = 0;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isClaimed() {
  return claims > 0;
}

function setClaims(next: number) {
  claims = next;
  listeners.forEach(listener => {
    listener();
  });
}

/** Held by a route while it renders its own `UnifiedShellFrame` outside the frame routes. */
export function useClaimUnifiedShellFrame(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    setClaims(claims + 1);
    return () => {
      setClaims(claims - 1);
    };
  }, [active]);
}

/** Whether a mounted route has claimed the frame, so the header leaves the bar to it. */
export function useUnifiedShellFrameClaimed() {
  return useSyncExternalStore(subscribe, isClaimed, isClaimed);
}
